import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Modal, Button, Badge } from "react-bootstrap";
import { Download, Pencil, Search, Trash2, Upload } from "lucide-react";
import { dashboardToast } from '../../utils/dashboardToast';
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import CapabilityViewModal from "./CapabilityViewModal";
import CapabilityCatalogTable from "../../components/sa/CapabilityCatalogTable";
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import { Alert, Button as UiButton } from "../../components/ui";
import {
  deleteCapability,
  exportCapability,
  getCapabilityCatalog,
  getCapabilityCatalogFilterOptions,
  getCapabilityDetail,
  importCapabilities,
} from "../../services/capabilitiesApi";
import { getBusinessTypeList } from "../../services/businessApi";
import {
  emptyMasterListFilters,
  MASTER_LIST_FILTER_ANY,
  type MasterListColumnKey,
  type MasterListQuestionPreview,
  type MasterListRow,
} from "../../utils/masterListUtils";
import {
  buildCapabilityCatalogQuery,
  CAPABILITY_CATALOG_PAGE_SIZE,
  getCapabilityCatalogFilterableColumns,
  mapCapabilityCatalogItemsToMasterList,
  sanitizeCatalogColumnFilters,
  type CapabilityCatalogFilterOptions,
} from "../../utils/capabilityCatalogUtils";
import { getFrameworks, getSourceTypes } from "../../services/frameworkApi";
import { type SourceTypeRef } from "../../utils/sourceTypeCatalog";
import {
  normalizeAssessmentFieldsFromApi,
  normalizeSubCapabilityFromApi,
  parseMaturityOptionsJson,
} from "../../utils/subCapabilityAssessmentConfig";
import { DEFAULT_ASSESSMENT_FORM_UI_CONFIG } from "../../utils/assessmentFormConfig";
import {
  capabilityCreatePath,
  capabilityEditPath,
  hasNamedSubCapabilities,
} from "../../components/sa/CapabilityForm/capabilityFormShared";
import "../../assets/css/dashboard.css";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";

const RDM_EMPTY = "\u2014";
const SUBTITLE_SEP = " \u00b7 ";

const renderQuestionStack = (questions) => {
  const items = (questions || []).filter((question) => question?.questionText?.trim());
  if (!items.length) {
    return <span className="rdm-empty">{RDM_EMPTY}</span>;
  }

  return (
    <div className="master-list-question-stack">
      {items.map((question, qIndex) => (
        <div key={question.id || qIndex} className="master-list-question-item">
          <span className="master-list-question-index">{qIndex + 1}.</span>
          <span>{question.questionText}</span>
        </div>
      ))}
    </div>
  );
};

const CapabilityManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState(null);
  const catalogReturnTo = `${location.pathname}${location.search}`;
  const [catalogRows, setCatalogRows] = useState<MasterListRow[]>([]);
  const [subsByCapabilityId, setSubsByCapabilityId] = useState(
    () => new Map<number, MasterListRow[]>()
  );
  const [catalogFilterOptions, setCatalogFilterOptions] = useState<CapabilityCatalogFilterOptions>(
    {}
  );
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogTotalCount, setCatalogTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get("search") || "");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [capabilityToDelete, setCapabilityToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewCapability, setViewCapability] = useState(null);
  const dataFetchedRef = useRef(false);
  const importInputRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  const [frameworkFilter, setFrameworkFilter] = useState(() => searchParams.get("frameworkId") || "");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [sourceTypes, setSourceTypes] = useState<SourceTypeRef[]>([]);
  const [columnFilters, setColumnFilters] = useState(() => emptyMasterListFilters("capability"));

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const catalogFilterableKeys = useMemo(
    () => getCapabilityCatalogFilterableColumns().map((column) => column.key),
    []
  );

  const catalogQuery = useMemo(
    () =>
      buildCapabilityCatalogQuery({
        pageNumber: catalogPage,
        search: debouncedSearch,
        frameworkId: frameworkFilter,
        sourceTypeId: sourceTypeFilter,
        columnFilters,
      }),
    [catalogPage, columnFilters, debouncedSearch, frameworkFilter, sourceTypeFilter]
  );

  const refreshCatalogMeta = useCallback(async () => {
    const [businessResult, frameworkResult, sourceTypeList] = await Promise.all([
      getBusinessTypeList(1, 500),
      getFrameworks(1, 500),
      getSourceTypes("", true),
    ]);

    const activeTypes = (businessResult.items || []).filter((bt) => bt.isActive !== false);
    setBusinessTypes(activeTypes);
    setFrameworks(frameworkResult.items || []);
    setSourceTypes((sourceTypeList || []) as SourceTypeRef[]);
  }, []);

  const refreshCatalogPage = useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      try {
        const filterQuery = buildCapabilityCatalogQuery({
          pageNumber: 1,
          pageSize: CAPABILITY_CATALOG_PAGE_SIZE,
          search: debouncedSearch,
          frameworkId: frameworkFilter,
          sourceTypeId: sourceTypeFilter,
          columnFilters,
        });

        const [pageResult, filterOptions] = await Promise.all([
          getCapabilityCatalog(catalogQuery),
          getCapabilityCatalogFilterOptions(filterQuery),
        ]);

        const sanitizedFilters = sanitizeCatalogColumnFilters(
          columnFilters,
          filterOptions,
          catalogFilterableKeys
        );
        if (sanitizedFilters !== columnFilters) {
          setColumnFilters(sanitizedFilters);
        }
        setCatalogFilterOptions(filterOptions);

        const { rows, subsByCapabilityId: subsMap } = mapCapabilityCatalogItemsToMasterList(
          pageResult.items
        );
        setCatalogRows(rows);
        setSubsByCapabilityId(subsMap);
        setCatalogTotalCount(pageResult.totalCount);

        const totalPages = Math.max(
          1,
          Math.ceil(pageResult.totalCount / CAPABILITY_CATALOG_PAGE_SIZE)
        );
        if (catalogPage > totalPages) {
          setCatalogPage(totalPages);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch capabilities. Please try again.";
        setError(message);
        setCatalogRows([]);
        setSubsByCapabilityId(new Map());
        setCatalogTotalCount(0);
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [
      catalogFilterableKeys,
      catalogPage,
      catalogQuery,
      columnFilters,
      debouncedSearch,
      frameworkFilter,
      sourceTypeFilter,
    ]
  );

  const refreshCatalog = useCallback(
    (showLoading = true) => refreshCatalogPage(showLoading),
    [refreshCatalogPage]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCatalogPage(1);
  }, [debouncedSearch, frameworkFilter, sourceTypeFilter]);

  useEffect(() => {
    void refreshCatalogMeta();
    dataFetchedRef.current = true;
  }, [refreshCatalogMeta]);

  useEffect(() => {
    refreshCatalog();
  }, [refreshCatalog]);

  useEffect(() => {
    const state = location.state as { refreshCatalog?: boolean } | null;
    if (!state?.refreshCatalog) return;
    refreshCatalog(false);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate, refreshCatalog]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFrameworkFilterChange = (nextFilter: string) => {
    setFrameworkFilter(nextFilter);
  };

  const catalogTotalPages = useMemo(
    () => Math.max(1, Math.ceil(catalogTotalCount / CAPABILITY_CATALOG_PAGE_SIZE)),
    [catalogTotalCount]
  );

  const handleCatalogPageChange = useCallback(
    (nextPage: number) => {
      const page = Math.min(Math.max(1, nextPage), catalogTotalPages);
      setCatalogPage(page);
    },
    [catalogTotalPages]
  );

  const handleCatalogColumnFilterChange = useCallback(
    (key: MasterListColumnKey, value: string) => {
      setColumnFilters((prev) => ({ ...prev, [key]: value }));
      setCatalogPage(1);
    },
    []
  );

  const handleClearCatalogColumnFilters = useCallback(() => {
    setColumnFilters(emptyMasterListFilters("capability"));
    setFrameworkFilter("");
    setSourceTypeFilter("");
    setCatalogPage(1);
  }, []);

  const columnFilterValue = (key: MasterListColumnKey) => {
    const selected = columnFilters[key];
    return !selected || selected === MASTER_LIST_FILTER_ANY ? "" : selected;
  };

  const setColumnFilterValue = (key: MasterListColumnKey, value: string) => {
    handleCatalogColumnFilterChange(key, value || MASTER_LIST_FILTER_ANY);
  };

  const frameworkOptions = frameworks.map((framework) => ({
    value: String(framework.id),
    label: framework.sourceFrameworkName,
  }));

  const optionList = (key: MasterListColumnKey) =>
    (catalogFilterOptions[key] || [])
      .filter((option) => option && option !== "—")
      .map((option) => ({ value: option, label: option }));

  const sourceTypeOptions = sourceTypes
    .filter((type) => type.isActive !== false)
    .map((type) => ({
      value: String(type.id),
      label: type.name,
    }));

  const filtersActive =
    Boolean(frameworkFilter) ||
    Boolean(sourceTypeFilter) ||
    ["capabilityIsActive", "frameworkReferenceCompany", "businessTypeName"].some(
      (key) => columnFilterValue(key as MasterListColumnKey)
    );

  const frameworkSummaryCount = (catalogFilterOptions.sourceFrameworkName || []).filter(
    (name) => name && name !== "—"
  ).length;

  const loadCatalogQuestionPreview = useCallback(
    async (sub: MasterListRow): Promise<MasterListQuestionPreview | null> => {
      try {
        const detail = await getCapabilityDetail(sub.capabilityId);
        const subCap = (detail.subCapabilities || []).find(
          (item) => Number(item.subId ?? item.id) === sub.subId
        );
        if (!subCap) return null;

        const mapQuestions = (
          questions: Array<{ id?: number; questionText?: string }> | undefined,
          prefix: string
        ) =>
          (questions || [])
            .filter((question) => String(question?.questionText || "").trim())
            .map((question, index) => ({
              rowKey: `${prefix}-${sub.subId}-${question.id ?? index}`,
              viewMode: "question" as const,
              capabilityId: sub.capabilityId,
              capabilityName: sub.capabilityName,
              capabilityDescription: "",
              capabilityIsActive: sub.capabilityIsActive,
              frameworkId: sub.frameworkId,
              sourceFrameworkName: sub.sourceFrameworkName,
              frameworkReferenceCompany: sub.frameworkReferenceCompany,
              frameworkType: sub.frameworkType,
              frameworkDescription: "",
              businessTypeId: sub.businessTypeId,
              businessTypeName: sub.businessTypeName,
              subId: sub.subId,
              subCapabilityName: sub.subCapabilityName,
              subCapabilityDescription: "",
              subIsActive: sub.subIsActive,
              subCapabilityCount: 0,
              subCapabilitySummary: "",
              questionCount: 0,
              questionId: Number(question.id) || 0,
              questionText: String(question.questionText || ""),
              questionState: prefix === "current" ? "Current State" : "Future State",
              questionDisplayOrder: index,
              questionIsActive: true,
              currentStateQuestions: [],
              futureStateQuestions: [],
            }));

        return {
          title: sub.subCapabilityName || "Sub-capability questions",
          subtitle: [sub.capabilityName, sub.sourceFrameworkName].filter(Boolean).join(SUBTITLE_SEP),
          current: mapQuestions(subCap.currentStateQuestions, "current"),
          future: mapQuestions(subCap.futureStateQuestions, "future"),
          details: (() => {
            const normalized = normalizeSubCapabilityFromApi(subCap, sub.capabilityId);
            const framework =
              frameworks.find((fw) => fw.id === sub.frameworkId) ||
              frameworks.find((fw) => fw.sourceFrameworkName === sub.sourceFrameworkName) ||
              null;
            return {
              description: normalized.subCapabilityDescription,
              capabilityName: sub.capabilityName,
              userType: sub.businessTypeName,
              active: normalized.isActive !== false,
              framework,
              frameworkLabel: sub.sourceFrameworkName,
              sourceTypeName: sourceTypes.find(
                (t) => Number(t.id) === Number(framework?.sourceTypeId ?? framework?.SourceTypeId)
              )?.name,
              maturityOptions: Array.isArray(normalized.maturityOptions)
                ? normalized.maturityOptions
                : parseMaturityOptionsJson(normalized.maturityOptionsJson) ||
                  DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions,
              customMaturity: Boolean(normalized.useCustomMaturity),
              subCapability: normalized,
            };
          })(),
        };
      } catch {
        dashboardToast.error("Failed to load questions for this sub-capability.");
        return null;
      }
    },
    [frameworks, sourceTypes]
  );

  const getFrameworkLabel = useCallback(
    (capability) => {
      if (!capability) return "";
      if (capability.sourceFrameworkName) return capability.sourceFrameworkName;
      const frameworkId = capability.frameworkId;
      if (!frameworkId) return "";
      return frameworks.find((fw) => fw.id === frameworkId)?.sourceFrameworkName || "";
    },
    [frameworks]
  );

  const viewFramework = useMemo(() => {
    if (!viewCapability) return null;
    const label = getFrameworkLabel(viewCapability);
    return (
      frameworks.find((fw) => fw.id === viewCapability.frameworkId) ||
      frameworks.find((fw) => label && fw.sourceFrameworkName === label) ||
      null
    );
  }, [viewCapability, frameworks, getFrameworkLabel]);

  const openCreateCapability = useCallback(() => {
    navigate(capabilityCreatePath(), { state: { returnTo: catalogReturnTo } });
  }, [catalogReturnTo, navigate]);

  const openCapabilityForEdit = useCallback(
    (capabilityId: number) => {
      if (!capabilityId) return;
      navigate(capabilityEditPath(capabilityId), { state: { returnTo: catalogReturnTo } });
    },
    [catalogReturnTo, navigate]
  );

  useEffect(() => {
    if (!showDeleteModal && !showViewModal) {
      return undefined;
    }

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPaddingRight = body.style.paddingRight;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.paddingRight = previousBodyPaddingRight;
    };
  }, [showDeleteModal, showViewModal]);

  const handleDelete = useCallback(
    (capabilityId: number) => {
      if (!capabilityId) {
        dashboardToast.error("Capability not found for deletion");
        return;
      }
      const row = catalogRows.find((item) => item.capabilityId === capabilityId);
      setCapabilityToDelete({
        id: capabilityId,
        capabilityId,
        capabilityName: row?.capabilityName || "this capability",
      });
      setShowDeleteModal(true);
    },
    [catalogRows]
  );

  const handleExport = useCallback(async (capabilityId) => {
    try {
      await exportCapability(capabilityId);
    } catch (err) {
      dashboardToast.error(
        err instanceof Error ? err.message : "Failed to export capability."
      );
    }
  }, []);

  const handleImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    setIsImporting(true);
    try {
      const result = await importCapabilities(file);
      await refreshCatalog(false);
      const created = result.createdCount ?? 0;
      const errors = result.errors ?? [];
      const label = created === 1 ? "capability" : "capabilities";
      if (errors.length > 0) {
        dashboardToast.warning(`Imported ${created} ${label}. ${errors[0]}`);
      } else {
        dashboardToast.success(`Imported ${created} ${label}.`);
      }
    } catch (err) {
      dashboardToast.error(
        err instanceof Error ? err.message : "Failed to import capabilities."
      );
    } finally {
      setIsImporting(false);
    }
  }, [refreshCatalog]);

  const confirmDelete = useCallback(async () => {
    if (!capabilityToDelete) {
      return;
    }

    const capabilityId =
      capabilityToDelete.id ||
      capabilityToDelete.capabilityId ||
      capabilityToDelete.ID;

    if (!capabilityId) {
      dashboardToast.error("Capability not found for deletion");
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteCapability(capabilityId);

      // Update the table immediately so the deleted row disappears without a full-page spinner.
      await refreshCatalog(false);
      dashboardToast.success("Capability deleted successfully");
    } catch (err) {
      console.error("Error deleting capability:", err);
      const message =
        err instanceof Error ? err.message : "Failed to delete capability. Please try again.";
      dashboardToast.error(message);
      await refreshCatalog(false);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setCapabilityToDelete(null);
    }
  }, [capabilityToDelete, refreshCatalog]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setCapabilityToDelete(null);
  }, []);

  const openViewModal = useCallback((capability) => {
    if (!capability) return;
    const capabilityId =
      capability.id || capability.capabilityId || capability.ID || 0;
    const assessmentFields = normalizeAssessmentFieldsFromApi(capability);
    setViewCapability({
      ...capability,
      ...assessmentFields,
      currentStateQuestions:
        capability.currentStateQuestions || assessmentFields.currentStateQuestions || [],
      futureStateQuestions:
        capability.futureStateQuestions || assessmentFields.futureStateQuestions || [],
      subCapabilities: (capability.subCapabilities || []).map((sub) =>
        normalizeSubCapabilityFromApi(sub, capabilityId)
      ),
    });
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewCapability(null);
  }, []);

  const openCatalogRow = useCallback(
    async (row: MasterListRow) => {
      try {
        const capability = await getCapabilityDetail(row.capabilityId);
        openViewModal(capability);
      } catch (err) {
        dashboardToast.error(
          err instanceof Error ? err.message : "Failed to load capability details."
        );
      }
    },
    [openViewModal]
  );

  const renderCatalogActions = useCallback(
    (row: MasterListRow) => {
      const capabilityId = row.capabilityId;
      if (!capabilityId) return null;

      return (
        <>
          <UiButton
            variant="outline"
            size="sm"
            className="btn-list-edit"
            onClick={() => handleExport(capabilityId)}
            title="Export"
            aria-label={`Export ${row.capabilityName}`}
          >
            <Download />
          </UiButton>
          <UiButton
            variant="outline"
            size="sm"
            className="btn-list-edit"
            onClick={() => void openCapabilityForEdit(capabilityId)}
            title="Edit"
            aria-label={`Edit ${row.capabilityName}`}
          >
            <Pencil />
          </UiButton>
          <UiButton
            variant="danger"
            size="sm"
            className="btn-list-delete"
            onClick={() => handleDelete(capabilityId)}
            title="Delete"
            aria-label={`Delete ${row.capabilityName}`}
          >
            <Trash2 />
          </UiButton>
        </>
      );
    },
    [handleDelete, handleExport, openCapabilityForEdit]
  );

  return (
    <DashboardShell isMenuCollapsed={isMenuCollapsed}
      sidebar={<Menu onSelect={handleMenuSelection} selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />}>
      <SaListPageShell topBarTitle={FRAMEWORK_STUDIO_LABELS.capabilityCatalog}>
            <div className="dash-frame-parent1 cap-catalog-page">
              <header className="cap-catalog-page__head">
                <div className="cap-catalog-page__intro">
                  <h2 className="cap-catalog-page__title">
                    {FRAMEWORK_STUDIO_LABELS.capabilityCatalogManageTitle}
                  </h2>
                  <p className="cap-catalog-page__desc">
                    {FRAMEWORK_STUDIO_LABELS.capabilityCatalogManageDescription}
                  </p>
                </div>
                <div className="cap-catalog-page__actions">
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleImport}
                    style={{ display: "none" }}
                  />
                  <UiButton
                    variant="outline"
                    className="sa-create-btn"
                    onClick={() => importInputRef.current?.click()}
                    disabled={isImporting}
                  >
                    <Upload size={16} aria-hidden />
                    {isImporting ? "Importing\u2026" : "Import Excel"}
                  </UiButton>
                  <UiButton variant="primary" className="sa-create-btn" onClick={openCreateCapability} disabled={isImporting}>
                    Create Capability
                  </UiButton>
                </div>
              </header>

              <div className="cap-catalog-page__table-shell master-list-page-content">
                <div className="cap-catalog-toolbar">
                  <div className="dash-search-field cap-catalog-toolbar__search">
                    <Search className="dash-search-field__icon" aria-hidden />
                    <input
                      type="text"
                      placeholder="Search capabilities…"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="dash-search-field__input"
                      disabled={isImporting}
                    />
                  </div>
                  <div className="cap-catalog-toolbar__filters">
                    <CatalogFilterChip
                      label="Framework"
                      value={frameworkFilter}
                      options={frameworkOptions}
                      onChange={handleFrameworkFilterChange}
                    />
                    <CatalogFilterChip
                      label={FRAMEWORK_STUDIO_LABELS.sourceType}
                      value={sourceTypeFilter}
                      options={sourceTypeOptions}
                      onChange={setSourceTypeFilter}
                    />
                    <CatalogFilterChip
                      label="Status"
                      value={columnFilterValue("capabilityIsActive")}
                      options={optionList("capabilityIsActive")}
                      onChange={(value) => setColumnFilterValue("capabilityIsActive", value)}
                    />
                    <CatalogFilterChip
                      label="Ref. company"
                      value={columnFilterValue("frameworkReferenceCompany")}
                      options={optionList("frameworkReferenceCompany")}
                      onChange={(value) => setColumnFilterValue("frameworkReferenceCompany", value)}
                    />
                    <CatalogFilterChip
                      label="User type"
                      value={columnFilterValue("businessTypeName")}
                      options={optionList("businessTypeName")}
                      onChange={(value) => setColumnFilterValue("businessTypeName", value)}
                    />
                    {filtersActive ? (
                      <button type="button" className="cap-catalog-toolbar__clear" onClick={handleClearCatalogColumnFilters}>
                        Clear all
                      </button>
                    ) : null}
                  </div>
                  <span className="cap-catalog-toolbar__meta">
                    {catalogTotalCount === 1 ? "1 capability" : `${catalogTotalCount} capabilities`}
                    {frameworkSummaryCount > 0
                      ? ` · ${frameworkSummaryCount === 1 ? "1 framework" : `${frameworkSummaryCount} frameworks`}`
                      : ""}
                  </span>
                </div>
                {loading ? (
                  <ContentLoader label="Loading\u2026" />
                ) : error ? (
                  <Alert variant="error">{error}</Alert>
                ) : (
                  <CapabilityCatalogTable
                    rows={catalogRows}
                    subsByCapabilityId={subsByCapabilityId}
                    columnFilters={columnFilters}
                    onColumnFilterChange={handleCatalogColumnFilterChange}
                    onClearColumnFilters={handleClearCatalogColumnFilters}
                    filterOptionsByKey={catalogFilterOptions}
                    page={catalogPage}
                    totalPages={catalogTotalPages}
                    totalCount={catalogTotalCount}
                    onPageChange={handleCatalogPageChange}
                    onRowClick={(row) => void openCatalogRow(row)}
                    renderActions={renderCatalogActions}
                    onLoadQuestionPreview={loadCatalogQuestionPreview}
                  />
                )}
              </div>
            </div>
      </SaListPageShell>


      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the capability "
          {capabilityToDelete?.capabilityName}" and all of its sub-capabilities?
          <br />
          <small className="text-muted">
            Capabilities that are selected in a project or used in assessments cannot be deleted.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={cancelDelete}
            style={{ width: "auto" }}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-dashboard-primary"
            onClick={confirmDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <CapabilityViewModal
        open={showViewModal}
        capability={viewCapability}
        frameworkLabel={getFrameworkLabel(viewCapability) || ""}
        framework={viewFramework}
        sourceTypeName={
          sourceTypes.find((t) => Number(t.id) === Number(viewFramework?.sourceTypeId ?? viewFramework?.SourceTypeId))
            ?.name
        }
        userTypeLabel={
          businessTypes.find((bt) => bt.id === viewCapability?.businessTypeId)?.name ||
          String(viewCapability?.businessTypeId ?? "")
        }
        onClose={closeViewModal}
        onEdit={() => {
          const capability = viewCapability;
          closeViewModal();
          if (capability) {
            openCapabilityForEdit(Number(capability.id || capability.capabilityId || capability.ID));
          }
        }}
        onDelete={() => {
          const capability = viewCapability;
          closeViewModal();
          if (capability) {
            handleDelete(capability.id || capability.capabilityId || capability.ID);
          }
        }}
      />

    </DashboardShell>
  );
};

export default CapabilityManagement;
